/**
 * Server <-> client chat message adapters for useChat.
 */

import type { ServerChatMessage } from '@/lib/api/endpoints/chat'
import type { ChatMessage } from '../hooks/useChat.types'
import { sanitizeHiddenDirectives } from './hidden-directives'
import { RESULT_CARDS_MARKER } from '../components/result-cards/types'
import { stripInternalUUIDs } from '@/common/lib/sanitize-uuids'

/** Robustly extract a created object ID from a tool_result payload.
 *  Handles strings, nested JSON strings, objects, and numeric IDs. */
export function extractCreatedObjectId(payload: unknown): string | undefined {
  let current = payload
  for (let depth = 0; depth < 4 && current != null; depth++) {
    if (typeof current === 'number') return String(current)
    if (typeof current === 'string') {
      const text = current.trim()
      if (!text) return undefined
      if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
        try { current = JSON.parse(text); continue } catch { return undefined }
      }
      return undefined
    }
    if (Array.isArray(current)) { current = current[0]; continue }
    if (typeof current === 'object') {
      const obj = current as Record<string, unknown>
      const directId = obj.id ?? (obj.data as any)?.id ?? (obj.object as any)?.id ?? (obj.created as any)?.id
      if (typeof directId === 'string' || typeof directId === 'number') return String(directId)
      if (obj.result !== undefined) { current = obj.result; continue }
      if (obj.content !== undefined) { current = obj.content; continue }
      return undefined
    }
  }
  return undefined
}

/**
 * Convert server message to client message format.
 *
 * The persisted assistant content may end with a {@link RESULT_CARDS_MARKER}
 * block (`prose + '\n' + MARKER + JSON.stringify(cards)`). `sanitizeHiddenDirectives`
 * scans for `{{XAVIOR_*}}` directive tokens and excises each token plus its JSON
 * braces — so if an email subject/snippet INSIDE the serialized cards JSON ever
 * contained that literal substring, sanitizing the whole string would slice into
 * the card JSON and corrupt it (then `splitResultCards` drops the cards on
 * reload). Split the marker block off FIRST, sanitize only the prose, then
 * re-attach the untouched card block. Mirrors the server invariant that the
 * marker is appended AFTER sanitization and never fed back through a sanitizer.
 */
export function serverToClientMessage(msg: ServerChatMessage): ChatMessage {
  const raw = String(msg.content || '')
  const markerIndex = raw.indexOf(RESULT_CARDS_MARKER)
  const body = markerIndex === -1 ? raw : raw.slice(0, markerIndex)
  const cardBlock = markerIndex === -1 ? '' : raw.slice(markerIndex)
  const sanitized = sanitizeHiddenDirectives(body)
  return {
    id: msg.id,
    role: msg.role as 'user' | 'assistant',
    content: sanitized.content + cardBlock,
    timestamp: new Date(msg.createdAt),
    thinking: msg.thinking ? stripInternalUUIDs(msg.thinking) : undefined,
    toolsUsed: msg.toolsUsed,
    routing: msg.routing,
    model: undefined,
    durationMs: msg.durationMs,
    mode: msg.mode,
    attachments: msg.attachments?.map(a => ({
      filename: a.filename,
      fileType: a.fileType,
      assetId: a.assetId,
      url: a.url,
    })),
  }
}

